// [목적] PlanItem 재배열 mutation — 낙관적 업데이트 + onError 롤백 + onSettled invalidate
// [이유] fe-screens-skeleton ADR-6 — 드래그앤드롭 UX는 낙관적 반영이 필수, 실패 시 원복
// [사이드 이팩트] queryClient.setQueryData 로 ['folio', id] 캐시를 임시 갱신
// [수정 시 고려사항] BE reorder 실패 시(예: count mismatch) 사용자에게 토스트 알림 추가

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Folio, ReorderItemsInput } from '@gangwoncanvas/types';

import { reorderItems } from '../api/folioClient.js';
import { queryKeys } from '../api/queryKeys.js';

interface ReorderArgs {
  folioId: string;
  input: ReorderItemsInput;
}

export function useReorderItems() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ folioId, input }: ReorderArgs) => reorderItems(folioId, input),
    onMutate: async ({ folioId, input }) => {
      await qc.cancelQueries({ queryKey: queryKeys.folio(folioId) });
      const prev = qc.getQueryData<Folio>(queryKeys.folio(folioId));
      if (prev?.days) {
        const next: Folio = {
          ...prev,
          days: prev.days.map((d) => {
            if (d.id !== input.dayPlanId) return d;
            const byId = new Map(d.items.map((it) => [it.id, it]));
            const reordered = input.itemIds
              .map((id) => byId.get(id))
              .filter((it): it is NonNullable<typeof it> => Boolean(it))
              .map((it, i) => ({ ...it, ord: i + 1 }));
            return { ...d, items: reordered };
          }),
        };
        qc.setQueryData(queryKeys.folio(folioId), next);
      }
      return { prev };
    },
    onError: (_err, { folioId }, context) => {
      if (context?.prev) qc.setQueryData(queryKeys.folio(folioId), context.prev);
    },
    onSettled: (_data, _err, { folioId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
    },
  });
}
